/**
 * Address bar — the URL field, its reachability badge, and the external open.
 *
 * @module BrowserAddressBar
 */
import { ExternalLinkIcon } from "lucide-react";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";
import type { BrowserChromeProps } from "./BrowserChrome";
import type { BrowserPreviewAccessTarget } from "./browserShellTypes";

export function BrowserAccessTargetBadge(props: { readonly target: BrowserPreviewAccessTarget }) {
  if (!props.target.displayUrl) {
    return null;
  }
  return (
    <span className="hidden h-7 shrink-0 items-center rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground xl:inline-flex">
      {props.target.displayLabel}
    </span>
  );
}

export function BrowserAddressForm(props: BrowserChromeProps) {
  return (
    <form className="min-w-0 flex-1" onSubmit={(event) => void props.onNavigate(event)}>
      <div className="group/address relative">
        <input
          ref={props.addressInputRef}
          className="h-7 w-full rounded-md border border-transparent bg-transparent px-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground hover:bg-muted/40 focus:border-border focus:bg-background disabled:opacity-50 group-hover/address:has-[+button]:pe-8"
          value={props.addressValue}
          onChange={(event) => props.onSetAddressValue(event.currentTarget.value)}
          onFocus={(event) => {
            props.onSetAddressValue(event.currentTarget.value);
            props.addressInputFocusedRef.current = true;
            props.onSetAddressInputFocused(true);
          }}
          onBlur={() => {
            props.addressInputFocusedRef.current = false;
            props.onSetAddressInputFocused(false);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" || props.browserAddressBusy) {
              return;
            }
            event.preventDefault();
            void props.onSubmitBrowserAddress("navigate-active-tab", event.currentTarget.value);
          }}
          aria-label="Browser URL"
          placeholder="Search or enter URL"
          disabled={props.browserAddressBusy}
        />
        <BrowserOpenExternalButton
          openExternalUrl={props.browserAccessTarget.openExternalUrl}
          onOpenExternal={props.onOpenExternal}
        />
      </div>
    </form>
  );
}

function BrowserOpenExternalButton(props: {
  readonly openExternalUrl: string | null;
  readonly onOpenExternal: (url: string) => void;
}) {
  if (!props.openExternalUrl) {
    return null;
  }
  const url = props.openExternalUrl;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="pointer-events-none absolute right-0.5 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted/60 hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover/address:pointer-events-auto group-hover/address:opacity-100"
            aria-label="Open in Chrome"
            onClick={() => props.onOpenExternal(url)}
          >
            <ExternalLinkIcon className="size-3.5" />
          </button>
        }
      />
      <TooltipPopup side="bottom">Open in Chrome</TooltipPopup>
    </Tooltip>
  );
}
